import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { CaseFilterBar, CaseIds } from "../components/CaseFilterBar";
import { matchCase, caseKind } from "../lib/caseSearch";
import { RiskBadge, RecommendationCard, MedicalDisclaimer } from "../components/clinical";
import { CaseEmergencies } from "../components/emergency/CaseEmergencies";
import { FinalDecision, DetailsToggle } from "../components/FinalDecision";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Eye, Download, FolderClock, PlusCircle } from "lucide-react";

export default function CaseHistory() {
  const [cases, setCases] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const load = () => api.get("/cases").then(({ data }) => setCases(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/cases/${id}`);
      setCases((c) => c.filter((x) => x.id !== id));
      toast.success("Case deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/cases/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `referral_${id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate PDF");
    }
  };

  const filtered = cases.filter((c) => matchCase(c, q) && (filter === "all" || caseKind(c) === filter));
  const count = (k) => cases.filter((c) => caseKind(c) === k).length;

  return (
    <div data-testid="case-history-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Case History</h1>
          <p className="text-slate-500 mt-1">{cases.length} saved case{cases.length !== 1 ? "s" : ""}</p>
        </div>
        <Link to="/app/new"><Button className="bg-slate-900 hover:bg-slate-800"><PlusCircle className="w-4 h-4 mr-2" /> New</Button></Link>
      </div>

      <CaseFilterBar q={q} setQ={setQ} filter={filter} setFilter={setFilter}
        options={[["all", "All"], ["labs", "Awaiting results"], ["specialist", "Awaiting specialist"], ["completed", "Completed"]]}
        counts={{ all: cases.length, labs: count("labs"), specialist: count("specialist"), completed: count("completed") }} />

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center mt-6">
          <FolderClock className="w-9 h-9 text-slate-300 mx-auto" />
          <p className="text-slate-500 mt-3">No cases found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 mt-6 overflow-hidden">
          <div className="divide-y divide-slate-100">
            {filtered.map((c) => (
              <div key={c.id} data-testid={`case-row-${c.id}`} className="flex items-center gap-4 px-5 py-4 flex-wrap sm:flex-nowrap">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate">{c.patient?.name || "Unnamed patient"}</p>
                  <CaseIds c={c} />
                  <p className="text-sm text-slate-500 truncate">{c.procedure_name} · {new Date(c.created_at).toLocaleString()}</p>
                </div>
                {c.status === "pending" && (
                  <Link to={`/app/cases/${c.id}/results`} data-testid={`case-pending-${c.id}`} className="text-xs font-semibold bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full hover:bg-amber-200">Pending · enter results</Link>
                )}
                <RiskBadge status={c.decision?.overall_status || "green"} />
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setActive(c)} data-testid={`case-view-${c.id}`}><Eye className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => downloadPdf(c.id)} data-testid={`case-pdf-${c.id}`}><Download className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)} data-testid={`case-delete-${c.id}`} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto hide-scrollbar">
          <DialogHeader>
            <DialogTitle className="font-heading">{active?.patient?.name || "Case"} — {active?.procedure_name}</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600 grid grid-cols-2 gap-2">
                <p><b>Age:</b> {active.patient?.age || "—"}</p>
                <p><b>Sex:</b> {active.patient?.sex || "—"}</p>
                <p><b>Case no.:</b> {active.case_number || "—"}</p>
                <p><b>File no.:</b> {active.patient?.file_no || "—"}</p>
                <p className="col-span-2"><b>Phone:</b> <span dir="ltr">{active.patient?.phone || "—"}</span></p>
                <p className="col-span-2"><b>Conditions:</b> {active.conditions_names?.join(", ") || "None"}</p>
                <p className="col-span-2"><b>Medications:</b> {active.medications_names?.join(", ") || "None"}</p>
              </div>
              {active.referral_question && <p className="text-sm bg-slate-50 rounded-lg p-3"><b>Referral question:</b> {active.referral_question}</p>}
              <FinalDecision fd={active.decision?.final_decision} procedureName={active.procedure_name} />
              <CaseEmergencies recs={active.decision?.recommendations} />
              {active.decision?.final_decision ? (
                <DetailsToggle count={active.decision?.recommendations?.length || 0}>
                  {active.decision?.recommendations?.map((rec) => <RecommendationCard key={rec.item_id} rec={rec} />)}
                </DetailsToggle>
              ) : (
                <div className="space-y-3">
                  {active.decision?.recommendations?.map((rec) => <RecommendationCard key={rec.item_id} rec={rec} />)}
                </div>
              )}
              <MedicalDisclaimer testid="case-medical-disclaimer" />
              <Button onClick={() => downloadPdf(active.id)} className="bg-slate-900 hover:bg-slate-800"><Download className="w-4 h-4 mr-2" /> Download consultation report PDF</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
